import React from 'react';
import Module from 'Components/Core/Module';
import MarketdataGroupSelector from './MarketdataGroupSelector';
import MarketdataWatchlistAnalysis from './MarketdataWatchlistAnalysis';
import BrokerageBuyFromWatchlist from 'Components/Banking/BrokerageBuyFromWatchlist';
import IbBrokerageBuyFromWatchlist from 'Components/Banking/IbBrokerageBuyFromWatchlist';
import { setMarketdataForcedGroup, useMarketdataForcedGroup } from './marketdataForcedGroup';
import './Marketdata.css';

// Kombiniert Page Visibility API (document.hidden - deckt Tab-Wechsel/
// Minimieren ab) UND Fenster-Fokus (document.hasFocus() - deckt zusaetzlich
// "auf ein anderes Programm umgeschaltet, Browser-Fenster bleibt aber
// technisch sichtbar" ab) - Chat vom 2026-08-05: document.hidden allein
// erkannte einen App-/Fensterwechsel ohne Tab-Wechsel nicht zuverlaessig.
function isAppInBackground() {
	return document.hidden || !document.hasFocus();
}

// Land je Boerse, nur fuer die Handelsplatz-Heuristik in BrokerageOrderForm
// (isHomeVenue()) - zuverlaessiger als das ISIN-Laenderpraefix, wenn ein
// Unternehmen woanders eingetragen ist als es primaer notiert (z.B. "Meridian
// Mining PLC", britische ISIN GB..., aber an der kanadischen TSX gehandelt -
// Chat vom 2026-07-26). Nur die Boersen, die in dieser Watchlist tatsaechlich
// vorkommen (siehe auch CANADIAN_EXCHANGES/EXCHANGE_CURRENCY oben/im Backend).
const EXCHANGE_COUNTRY = {
	TSX: 'CA', TSXV: 'CA', NEO: 'CA', CNQ: 'CA',
	NASDAQ: 'US', NYSE: 'US', AMEX: 'US', US: 'US',
	ASX: 'AU', LSE: 'GB', WSE: 'PL', SIX: 'CH',
};


// Lazy geladen (Code-Splitting) statt normaler Import - MarketdataDetailPanel
// zieht recharts nach, eine vergleichsweise schwere Chart-Bibliothek. Da alle
// Module beim App-Start gemountet werden (siehe Content.js), wuerde ein
// normaler Import recharts bei JEDEM Reload mitladen/parsen, auch wenn
// "Live-Kurse" gar nicht aktiv ist - das machte den Reload spuerbar
// langsamer. Mit React.lazy() laedt der Code erst, wenn tatsaechlich ein
// Symbol angeklickt und das Panel gerendert wird.
const MarketdataDetailPanel = React.lazy(() => import('./MarketdataDetailPanel'));

// EODHDs Echtzeit-Endpunkt liefert grundsaetzlich kein Bid/Ask (anders als
// frueher bei FMP, wo es zumindest fuer US-Boersen verfuegbar war) - daher
// hier ein einheitlicher Hinweis fuer alle Boersen statt der frueheren
// Kanada-Sonderbehandlung.
function bidAskHint() {
	return 'Bid/Ask nicht verfügbar';
}

// Laedt beim Mount einen Snapshot (letzter bekannter Kurs je Symbol aus dem
// Redis-Cache, siehe MarketdataQuotes.inc) und haengt sich danach direkt an
// die bestehende Socket.IO-Verbindung (x.was.webSocket, siehe
// WebappWebSocket.js) fuer laufende Live-Updates - kein Polling.
// Desktop-Layout (ab 1025px, App-weite Grenze): Tabelle links (Hauptbereich) + Detail-/Chart-
// Panel rechts fuer das per Klick ausgewaehlte Symbol. Darunter (schmaler
// Viewport) stapelt sich das Panel unter die Tabelle statt daneben.
export default function MarketdataQuotes(props) {

	const x = props.x;
	const [quotes, setQuotes] = React.useState({});
	const previousPrices = React.useRef({});
	const [flashSymbols, setFlashSymbols] = React.useState({});
	const flashTimers = React.useRef({});
	const [selectedSymbol, setSelectedSymbol] = React.useState(null);
	// Zaehlt jeden Tabellen-Klick hoch (Chat vom 2026-08-11), auch auf das schon
	// ausgewaehlte Wertpapier - damit MarketdataDetailPanel die Charttechnik-
	// Analyse bei JEDEM Klick neu berechnet, nicht nur bei Symbolwechsel.
	const [selectionTick, setSelectionTick] = React.useState(0);
	const [reloading, setReloading] = React.useState(false);
	// Charttechnische Gesamtanalyse der Watchlist (Chat vom 2026-08-10).
	const [showAnalysis, setShowAnalysis] = React.useState(false);
	// Suchfilter fuer die Kurzbezeichnung (Chat vom 2026-08-10).
	const [filterText, setFilterText] = React.useState('');
	const filterInputRef = React.useRef(null);
	const [buyTarget, setBuyTarget] = React.useState(null);
	const [ibBuyTarget, setIbBuyTarget] = React.useState(null);
	const symbolOrder = React.useRef([]);
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);
	// Vollbild-Chart (Expand-Icon in MarketdataDetailPanel.js neben der
	// Zeitauswahl) - blendet die Tabelle aus, das Detail-/Chart-Panel nimmt
	// dann die komplette Breite ein. State liegt hier (nicht im Panel selbst),
	// weil das Ausblenden der Tabelle den ELTERN-Layout betrifft.
	const [chartExpanded, setChartExpanded] = React.useState(false);
	// Erzwungener Watchlist-Wechsel von aussen (siehe marketdataForcedGroup.js) -
	// "Watchlist Live anzeigen"-Icon in MarketdataWatchlist.js setzt das,
	// BEVOR dieses Modul aktiviert wird.
	const [forcedGroup, setForcedGroup] = React.useState(null);
	const consumeForcedGroup = useMarketdataForcedGroup(x, setForcedGroup);
	// Handelszeiten je "exchange"-Wert (siehe MarketdataMarketHours.inc) - hier
	// einmal pro Watchlist geladen und an das Detail-Panel weitergereicht,
	// damit dieses nicht denselben Request nochmal separat abfeuert.
	const [marketHours, setMarketHours] = React.useState({});

	function loadMarketHours(exchanges) {
		const uniqueExchanges = [...new Set(exchanges.filter(Boolean))];
		if (uniqueExchanges.length === 0) {
			return;
		}
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataMarketHours', { exchanges: uniqueExchanges.join(',') }, (result) => {
			if (result.data.returncode !== 1) {
				return;
			}
			const byExchange = {};
			(result.data.content || []).forEach((row) => {
				byExchange[row.exchange] = row;
			});
			setMarketHours(byExchange);
		});
	}

	// Kurze visuelle Rueckmeldung ("hier kam gerade ein Update rein"), da bei
	// kurzem Poll-Intervall (siehe marketdataWorker.mjs) sonst nicht auffaellt,
	// wann genau sich ein Wert aktualisiert. Faerbung nach Richtung
	// (hoch/runter/gleich), automatisches Ausblenden nach 1s - rein CSS-basiert,
	// keine Hover-Abhaengigkeit, daher auch auf Touch-Geraeten sichtbar.
	function triggerFlash(symbol, direction) {
		setFlashSymbols((current) => ({ ...current, [symbol]: direction }));
		if (flashTimers.current[symbol]) {
			clearTimeout(flashTimers.current[symbol]);
		}
		flashTimers.current[symbol] = setTimeout(() => {
			setFlashSymbols((current) => {
				const next = { ...current };
				delete next[symbol];
				return next;
			});
			delete flashTimers.current[symbol];
		}, 1000);
	}

	React.useEffect(() => {
		const timers = flashTimers.current;
		return () => {
			Object.values(timers).forEach((timer) => clearTimeout(timer));
		};
	}, []);

	function loadSnapshot(onDone) {
		if (selectedGroupId === null) {
			if (onDone) onDone();
			return;
		}
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataQuotes', { groupid: selectedGroupId }, (result) => {
			if (result.data.returncode !== 1) {
				if (onDone) onDone();
				return;
			}
			const rows = result.data.content || [];
			const initial = {};
			rows.forEach((row) => {
				initial[row.symbol] = row;
			});
			// Reihenfolge der Watchlist (sortorder, siehe MarketdataQuotes.inc)
			// separat merken - der quotes-State ist ein Objekt (Zugriff per
			// Symbol fuer Live-Updates), Objektschluessel haben aber keine
			// verlaessliche Reihenfolge fuer die Anzeige.
			symbolOrder.current = rows.map((row) => row.symbol);
			// Ersetzt den kompletten State (nicht mergen) - dadurch verschwinden
			// zwischenzeitlich aus der Watchlist geloeschte Symbole automatisch,
			// und neu hinzugefuegte tauchen auf.
			setQuotes(initial);
			loadMarketHours(rows.map((row) => row.exchange));
			// Beim (erneuten) Laden immer ein Symbol ausgewaehlt lassen - bleibt die
			// bisherige Auswahl gueltig, sonst faellt es auf das erste Symbol der
			// Watchlist zurueck, statt auf "nichts ausgewaehlt" (der Nutzer soll
			// beim Oeffnen sofort einen Chart sehen, nicht erst klicken muessen).
			setSelectedSymbol((current) => (current && initial[current]) ? current : (rows[0]?.symbol ?? null));
			if (onDone) onDone();
		});
	}

	// Manueller Reload-Button - Watchlist-Aenderungen (Symbol hinzugefuegt/
	// entfernt) kommen nur ueber diesen Weg in eine bereits geoeffnete
	// Live-Kurse-Ansicht, da der Live-Update-Kanal (marketdata-quote) nur
	// Kursaenderungen fuer bereits bekannte Symbole liefert, keine
	// Watchlist-Strukturaenderungen.
	function reload() {
		setReloading(true);
		loadSnapshot(() => setReloading(false));
	}

	function handleQuote(args) {
		// Nur verarbeiten, wenn Kursdaten gerade das aktive/sichtbare Modul sind
		// (x.wam.current, siehe WebappModule.js) - Updates fuer ein Modul im
		// Hintergrund (z.B. waehrend man im Forum ist) werden ignoriert, statt
		// unnoetig State-Updates/Re-Renders fuer eine unsichtbare Tabelle
		// auszuloesen. Beim naechsten Aktivieren laedt loadSnapshot() ohnehin
		// den aktuellen Stand nach.
		//
		// ZUSAETZLICH document.hidden pruefen (Chat vom 2026-08-05, live
		// beobachtet: ~100% CPU trotz Hintergrund-Tab) - IG/Kraken/IB streamen
		// seit der Umstellung auf permanentes Hintergrund-Streaming (siehe
		// marketdataWorker.mjs::ensureIgStreaming()/ensureKrakenStreaming()/
		// ensureIbConnection()) UNGEFILTERT an alle verbundenen Sockets, auch
		// wenn der Server durch "marketdata-watchlist-view-inactive" laengst
		// weiss, dass niemand hinschaut - das stoppt nur EODHDs Server-Polling,
		// NICHT den Broadcast der bereits laufenden Streams. Ohne diese
		// zusaetzliche Pruefung verarbeitete/renderte ein app-intern noch
		// "aktives", aber tatsaechlich im Hintergrund-Tab liegendes Modul
		// weiterhin JEDEN einzelnen Kraken-Tick (bei aktivem BTC-Handel
		// mehrmals pro Sekunde) - Browser drosseln zwar Timer in Hintergrund-
		// Tabs, aber keine eingehenden WebSocket-Nachrichten.
		//
		// document.hidden ALLEIN reicht nicht (Chat vom 2026-08-05, Folge-
		// Beobachtung: hilft bei Tab-Wechsel, aber nicht beim App-/Fenster-
		// wechsel ohne Tab-Wechsel, z.B. auf ein anderes Programm umschalten,
		// waehrend der Browser-Tab technisch weiter "sichtbar" gerendert
		// bleibt) - siehe isAppInBackground() weiter unten, die zusaetzlich
		// den Fenster-Fokus (document.hasFocus()) prueft.
		if (x.wam.current !== props.id || isAppInBackground()) {
			return;
		}
		const data = typeof args === 'string' ? JSON.parse(args) : args;
		// Der Live-Update-Kanal liefert Kursaenderungen fuer ALLE Symbole aller
		// Watchlists (der Node-Worker kennt keine Gruppen, siehe marketdataWorker.mjs).
		// symbolOrder.current (statt des quotes-States direkt) fuer die
		// Zugehoerigkeits-Pruefung nutzen, da handleQuote nur EINMAL beim Mounten
		// registriert wird (socket.on in der []-Effect unten) und damit sonst ueber
        // eine veraltete/leere quotes-Referenz aus dem allerersten Render
		// pruefen wuerde (stale closure) - jedes Update waere faelschlich
		// verworfen worden. symbolOrder.current wird von loadSnapshot() bei jedem
		// (Neu-)Laden aktuell gehalten, ist also immer auf dem neuesten Stand.
		if (!symbolOrder.current.includes(data.symbol)) {
			return;
		}
		let direction = 'flat';
		setQuotes((current) => {
			if (!(data.symbol in current)) {
				return current;
			}
			const previousPrice = current[data.symbol]?.price;
			previousPrices.current[data.symbol] = previousPrice;
			direction = previousPrice == null || data.price === previousPrice ? 'flat' : (data.price > previousPrice ? 'up' : 'down');
			// Spread von current[symbol] zuerst, damit Felder, die der Live-
			// Update-Payload nicht mitliefert (z.B. shortlabel - kennt nur die
			// DB/der initiale Snapshot, nicht der Node-Worker), erhalten bleiben.
			return { ...current, [data.symbol]: { ...current[data.symbol], ...data } };
		});
		triggerFlash(data.symbol, direction);
	}

	// Server-Polling nur, solange diese Ansicht tatsaechlich sichtbar ist (siehe
	// marketdataWorker.mjs::setViewerActive() - EODHDs Tageskontingent wurde
	// sonst durch 24/7-Hintergrund-Polling unnoetig verbraucht, selbst wenn
	// niemand hinschaut). "active"/"inactive" signalisieren dem Node-Worker
	// ueber Socket.IO, ob mindestens ein Client gerade zuschaut - onShow/onHide
	// (siehe Module.js) decken das Ein-/Ausblenden ab, der Mount-Check hier
	// zusaetzlich den Fall "dieses Modul ist bereits beim Laden der Seite das
	// aktive Startmodul" (dafuer wird show() nie aufgerufen), der Unmount-
	// Cleanup ist ein Sicherheitsnetz falls das Modul waehrend es aktiv war
	// komplett entfernt wird (z.B. per "Schliessen"-Icon).
	//
	// LUECKE (Chat vom 2026-08-05, live beobachtet: >20% CPU-Last obwohl die
	// App im Hintergrund war): onShow/onHide decken nur die Sichtbarkeit
	// INNERHALB der App ab (Modulwechsel), NICHT den Fall, dass der Browser-
	// Tab/die App selbst in den Hintergrund geht (Tab-Wechsel, Minimieren,
	// Bildschirm sperren) - das Modul bleibt dabei aus App-Sicht weiter "aktiv
	// angezeigt", obwohl niemand hinschaut. Die Page Visibility API
	// (document.hidden/"visibilitychange") deckt genau das zusaetzlich ab.
	React.useEffect(() => {
		const connection = x.was.webSocket;
		if (connection) {
			connection.on('marketdata-quote', handleQuote);
			if (x.wam.current === props.id && !isAppInBackground()) {
				connection.emit('marketdata-watchlist-view-active');
			}
		}

		function handleBackgroundChange() {
			// Nur relevant, wenn dieses Modul gerade das angezeigte ist - sonst
			// wuerde ein Tab-/Fensterwechsel faelschlich ein Modul "aktivieren",
			// das app-intern gar nicht sichtbar ist. "blur" reagiert dabei auch
			// auf einen App-/Fensterwechsel OHNE Tab-Wechsel, "visibilitychange"
			// zusaetzlich auf Tab-Wechsel/Minimieren (siehe isAppInBackground()).
			if (x.wam.current !== props.id || !connection) {
				return;
			}
			connection.emit(isAppInBackground() ? 'marketdata-watchlist-view-inactive' : 'marketdata-watchlist-view-active');
		}
		document.addEventListener('visibilitychange', handleBackgroundChange);
		window.addEventListener('blur', handleBackgroundChange);
		window.addEventListener('focus', handleBackgroundChange);

		return () => {
			document.removeEventListener('visibilitychange', handleBackgroundChange);
			window.removeEventListener('blur', handleBackgroundChange);
			window.removeEventListener('focus', handleBackgroundChange);
			if (connection) {
				connection.off('marketdata-quote', handleQuote);
				connection.emit('marketdata-watchlist-view-inactive');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleShow() {
		consumeForcedGroup();
		// Fokus aufs Filterfeld nach dem Aktivieren des Moduls (Nutzerwunsch,
		// Chat vom 2026-08-10) - kleiner Timeout, da onShow feuert, bevor das
		// Modul sicher sichtbar/im DOM fokussierbar ist.
		setTimeout(() => {
			if (filterInputRef.current) {
				filterInputRef.current.focus();
			}
		}, 50);
		// Nur "aktiv" melden, wenn der Tab/die App gerade auch tatsaechlich
		// sichtbar ist - sonst wuerde z.B. ein programmatischer Modulwechsel
		// waehrend die App im Hintergrund ist faelschlich Polling anstossen
		// (siehe Page-Visibility-Ergaenzung oben, Chat vom 2026-08-05).
		if (x.was.webSocket && !isAppInBackground()) {
			x.was.webSocket.emit('marketdata-watchlist-view-active');
		}
	}

	function handleHide() {
		if (x.was.webSocket) {
			x.was.webSocket.emit('marketdata-watchlist-view-inactive');
		}
	}

	React.useEffect(() => {
		if (selectedGroupId !== null) {
			loadSnapshot();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroupId]);

	// Watchlist-Reihenfolge (sortorder) statt alphabetischer Sortierung -
	// symbolOrder.current kommt aus dem letzten loadSnapshot(); Object.keys(quotes)
	// nur als Fallback fuer Symbole, die (theoretisch) noch nicht darin stehen.
	const symbols = symbolOrder.current.filter((symbol) => symbol in quotes)
		.concat(Object.keys(quotes).filter((symbol) => !symbolOrder.current.includes(symbol)));

	// Suchfilter fuer die Kurzbezeichnung (Chat vom 2026-08-10, Nutzerwunsch) -
	// bei aktivem Filter Trennlinien ausblenden (sind keine "Treffer") und nur
	// Zeilen zeigen, deren Kurzbezeichnung (Fallback: Symbol) den Suchtext
	// enthaelt (Gross-/Kleinschreibung egal).
	const filterNeedle = filterText.trim().toLowerCase();
	const visibleSymbols = filterNeedle === ''
		? symbols
		: symbols.filter((symbol) => {
			const quote = quotes[symbol];
			if (!quote || quote.isseparator) return false;
			const label = (quote.shortlabel || quote.label || symbol || '').toLowerCase();
			return label.includes(filterNeedle);
		});

	function formatNumber(value, digits = 2) {
		return value == null ? '–' : value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
	}

	// Nutzervorgabe, Chat vom 2026-08-08: Nachkommastellen beim Kurs nur
	// anzeigen, wenn der Kurs unter 1.000 liegt (erst 10.000, dann auf
	// Nutzerwunsch enger gefasst) - bei grossen Werten (z.B. "29.840") wirken
	// drei Nachkommastellen unnoetig praezise/unruhig, bei kleinen Werten
	// (z.B. Silber "63,510") sind sie dagegen relevant.
	// Nur fuer die Kurs-Spalte selbst, Bid/Ask/Low/High behalten weiterhin
	// immer 3 Nachkommastellen (formatNumber() bleibt dafuer unveraendert).
	function formatPrice(value) {
		if (value == null) return '–';
		return formatNumber(value, Math.abs(value) < 1000 ? 3 : 0);
	}

	return (
		<Module
			id={props.id}
			x={x}
			classes="module-marketdata-quotes"
			title={props.title}
			display={props.display}
			displayTitle="d-block"
			onShow={handleShow}
			onHide={handleHide}
			titleToolbar={
				<span className="module-table-title-toolbar">
					{/* Watchlist-Auswahl sitzt jetzt rechts neben dem Filterfeld (siehe
					    unten). Der IB-Gateway-Status ist in die Top-Navigation ans
					    IB-Icon gewandert (Chat vom 2026-08-12) - hier nur noch die
					    Aktions-Icons. */}
					<i
						className="bi bi-pencil module-icon"
						title="Watchlist bearbeiten"
						onClick={() => {
							// Verwaltung soll dieselbe Watchlist zeigen, die hier in Live
							// gerade ausgewaehlt ist, statt immer auf die Default-Watchlist
							// zurueckzufallen (siehe marketdataForcedGroup.js).
							setMarketdataForcedGroup(x, selectedGroupId);
							x.wam.activate('marketdata-watchlist');
						}}
					/>
					<i
						className={'bi bi-arrow-repeat module-icon' + (reloading ? ' marketdata-reload-spinning' : '')}
						title="Watchlist-Änderungen laden"
						onClick={reload}
					/>
					{/* Charttechnische Gesamtanalyse aller Werte der Watchlist
					    (Chat vom 2026-08-10). */}
					<i
						className="bi bi-clipboard-data module-icon"
						title="Charttechnische Analyse der Watchlist"
						onClick={() => setShowAnalysis(true)}
					/>
				</span>
			}
		>
			<div className={'marketdata-layout' + (chartExpanded ? ' marketdata-layout-expanded' : '')}>
				{!chartExpanded &&
				<div className="marketdata-quotes-scroll">
					{/* Suchfilter Kurzbezeichnung (Chat vom 2026-08-10). */}
					<div className="marketdata-quotes-filter">
						<i className="bi bi-search"></i>
						<input
							ref={filterInputRef}
							type="text"
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Escape') { setFilterText(''); e.stopPropagation(); } }}
							placeholder="Kurzbezeichnung filtern…"
						/>
						{filterText !== '' &&
							<i className="bi bi-x-lg marketdata-quotes-filter-clear" title="Filter löschen" onClick={() => setFilterText('')}></i>
						}
						{/* Aktive Watchlist als Hinweis + Auswahl-Icon, rechts im Filterfeld
						    (Chat vom 2026-08-12) - aus der zu vollen Kopfzeile hierher. */}
						<MarketdataGroupSelector x={x} id={props.id} onSelect={setSelectedGroupId} forcedGroup={forcedGroup} inline={true} />
					</div>
					<table className="marketdata-quotes-table">
						<thead>
							<tr>
								<th>Kurzbez.</th>
								<th className="marketdata-col-numeric">Kurs</th>
								{/* Nutzervorgabe, Chat vom 2026-08-08: "+/-" statt "Veränderung" -
							    voller Text auf Mobil zu breit, drueckte die Kaufen-Spalte aus
							    dem sichtbaren Bereich. */}
							<th className="marketdata-col-numeric" title="Veränderung">+/-</th>
								<th className="marketdata-col-numeric">Bid</th>
								<th className="marketdata-col-numeric">Ask</th>
								<th className="marketdata-col-numeric">Low</th>
								<th className="marketdata-col-numeric">High</th>
								<th>Zeitpunkt</th>
								<th className="marketdata-col-buy">Kaufen</th>
							</tr>
						</thead>
						<tbody>
							{symbols.length === 0 &&
								<tr><td colSpan="10" className="marketdata-empty">Keine Kurse verfügbar. Watchlist prüfen oder kurz warten, bis die ersten Kurse eintreffen.</td></tr>
							}
							{symbols.length > 0 && visibleSymbols.length === 0 &&
								<tr><td colSpan="10" className="marketdata-empty">Kein Treffer für „{filterText}".</td></tr>
							}
							{visibleSymbols.map((symbol) => {
								const quote = quotes[symbol];
								if (quote.isseparator) {
									// Trennlinie (isseparator=1, siehe MarketdataQuotes.inc) - kein
									// Kurs, eine durchgehende Linie ueber die Kursspalten statt
									// Zellen, "symbol" ist hier nur der eindeutige Platzhalter-
									// Schluessel ("__separator_<watchlistid>"), keine echte Notierung.
									return (
										<tr key={symbol} className="marketdata-quotes-separator-row">
											{/* Optionaler Titel (Chat vom 2026-08-10) - zentriert
											    auf der Linie; ohne Titel schlichte Linie. */}
											<td colSpan="10">
												<div className="marketdata-quotes-separator">
													<span className="marketdata-quotes-separator-line" />
													{quote.label ? <span className="marketdata-quotes-separator-title">{quote.label}</span> : null}
												</div>
											</td>
										</tr>
									);
								}
								const previous = previousPrices.current[symbol];
								const direction = previous == null || quote.price === previous ? '' : (quote.price > previous ? 'marketdata-price-up' : 'marketdata-price-down');
								const changeDirection = quote.change == null ? '' : (quote.change > 0 ? 'marketdata-price-up' : quote.change < 0 ? 'marketdata-price-down' : '');
								const flashDirection = flashSymbols[symbol];
								const flashClass = flashDirection ? 'marketdata-row-flash marketdata-row-flash-' + flashDirection : '';
								const selectedClass = symbol === selectedSymbol ? ' marketdata-row-selected' : '';
								const hours = marketHours[quote.exchange];
								const marketClosed = hours && hours.isMarketOpen === false;
								return (
									<tr key={symbol} className={flashClass + selectedClass} onClick={() => { setSelectedSymbol(symbol); setSelectionTick((t) => t + 1); }}>
										<td className="marketdata-symbol">
											{quote.shortlabel || symbol}
											{quote.exchange &&
												<span className="marketdata-symbol-exchange" title={'Börse: ' + quote.exchange}> ({quote.exchange})</span>
											}
											{marketClosed &&
												<i className="bi bi-lock-fill marketdata-market-closed-icon" title="Markt geschlossen" />
											}
										</td>
										<td className={'marketdata-col-numeric marketdata-price ' + direction}>{formatPrice(quote.price)}</td>
										<td className={'marketdata-col-numeric ' + changeDirection}>
											{quote.change == null ? '–' : (
												<React.Fragment>
													{formatNumber(quote.change, 3)} ({formatNumber(quote.changePercentage, 2)}%)
												</React.Fragment>
											)}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">
											{quote.bidPrice != null ? formatNumber(quote.bidPrice, 3) : <span title={bidAskHint(quote.exchange)}>–</span>}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">
											{quote.askPrice != null ? formatNumber(quote.askPrice, 3) : <span title={bidAskHint(quote.exchange)}>–</span>}
										</td>
										<td className="marketdata-col-numeric marketdata-secondary">{formatNumber(quote.dayLow, 3)}</td>
										<td className="marketdata-col-numeric marketdata-secondary">{formatNumber(quote.dayHigh, 3)}</td>
										<td className="marketdata-timestamp">{quote.timestamp ? new Date(quote.timestamp).toLocaleTimeString('de-DE') : ''}</td>
										<td className="marketdata-col-buy">
											{/* Nur kaufbar, wenn der einmalige comdirect-Abgleich (siehe
											    BrokerageTask::reconcileWatchlist()) ein Wertpapier gefunden
											    hat - Rohstoffe/Krypto/Index oder nicht zugeordnete Symbole
											    zeigen das Icon weiterhin (statt es ganz auszublenden, sonst
											    liess sich das leicht mit dem Update-Punkt verwechseln), aber
											    ausgegraut und ohne Wirkung beim Klick. */}
											<img
												src="/comdirect-favicon.ico"
												alt="Comdirect"
												className={'marketdata-buy-broker-icon' + (quote.comdirectinstrumentid ? '' : ' marketdata-col-buy-disabled')}
												title={quote.comdirectinstrumentid ? 'Über Comdirect kaufen' : 'Nicht über Comdirect handelbar'}
												onClick={(e) => {
													e.stopPropagation();
													if (!quote.comdirectinstrumentid) {
														return;
													}
													setBuyTarget({
														instrumentId: quote.comdirectinstrumentid,
														instrumentLabel: quote.shortlabel || quote.label || symbol,
														instrumentIsin: quote.isin || null,
														custodyCountry: EXCHANGE_COUNTRY[quote.exchange] || null,
													});
												}}
											/>
											{/* Chat vom 2026-08-07 (Bugreport: Kauf-Dialog zeigte "Kaufen: MN"
											    statt "MNQ", Future nicht kaufbar) - baute den IB-Kontrakt bisher
											    selbst aus einer laengst veralteten Symbol-Suffix-Heuristik
											    zusammen (IB_STOCK_EXCHANGE, fest secType "STK", ".slice(0,
											    lastIndexOf('.'))" - bei Futures/Indizes OHNE Punkt im Symbol
											    (z.B. "MNQ") schnitt das faelschlich den letzten Buchstaben ab).
											    Jetzt der tatsaechlich in der Watchlist-Verwaltung gepflegte
											    IB-Kontrakt (siehe MarketdataQuotes.inc, IbContractField.js) -
											    funktioniert dadurch fuer STK/FUT/IND gleichermassen korrekt. */}
											<img
												src="/ib-logo-v5.png"
												alt="Interactive Brokers"
												className={'marketdata-buy-broker-icon marketdata-buy-broker-icon-ib' + (quote.ibcontract ? '' : ' marketdata-col-buy-disabled')}
												title={quote.ibcontract ? 'Über Interactive Brokers kaufen' : 'Nicht über IB handelbar'}
												onClick={(e) => {
													e.stopPropagation();
													if (!quote.ibcontract) {
														return;
													}
													setIbBuyTarget({
														symbol,
														label: quote.shortlabel || quote.label || symbol,
														price: quote.price ?? null,
														bid: quote.bidPrice ?? null,
														ask: quote.askPrice ?? null,
														contract: quote.ibcontract,
													});
												}}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				}
				{selectedSymbol
					? (
						<React.Suspense fallback={<div className="marketdata-detail-panel marketdata-detail-empty">Lädt…</div>}>
							<MarketdataDetailPanel
							x={x}
							symbol={selectedSymbol}
							selectionTick={selectionTick}
							quote={quotes[selectedSymbol]}
							marketHours={marketHours[quotes[selectedSymbol]?.exchange]}
							expanded={chartExpanded}
							onToggleExpanded={() => setChartExpanded((current) => !current)}
						/>
						</React.Suspense>
					)
					: (
						<div className="marketdata-detail-panel marketdata-detail-empty">
							Symbol in der Tabelle anklicken, um Chart und alle Details anzuzeigen.
						</div>
					)
				}
			</div>
			{buyTarget &&
				<BrokerageBuyFromWatchlist
					x={x}
					instrumentId={buyTarget.instrumentId}
					instrumentLabel={buyTarget.instrumentLabel}
					instrumentIsin={buyTarget.instrumentIsin}
					custodyCountry={buyTarget.custodyCountry}
					onClose={() => setBuyTarget(null)}
				/>
			}
			{ibBuyTarget &&
				<IbBrokerageBuyFromWatchlist
					x={x}
					symbol={ibBuyTarget.symbol}
					label={ibBuyTarget.label}
					price={ibBuyTarget.price}
					bid={ibBuyTarget.bid}
					ask={ibBuyTarget.ask}
					contract={ibBuyTarget.contract}
					onClose={() => setIbBuyTarget(null)}
				/>
			}
			{showAnalysis &&
				<MarketdataWatchlistAnalysis
					x={x}
					items={symbols
						.filter((s) => quotes[s] && !quotes[s].isseparator)
						.map((s) => ({ symbol: s, shortlabel: quotes[s].shortlabel || quotes[s].label || s, price: quotes[s].price }))}
					onClose={() => setShowAnalysis(false)}
				/>
			}
		</Module>
	)

};
